import { message } from "antd";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import { AppStrings } from "../../utils/constants/AppStrings";
import { getPrivateInstance } from "../_privateInstance";
import { useAsyncExport } from "../useAsyncExport";
import type { AsyncExportState } from "../useAsyncExport.types";

export type AalSection =
  | "all"
  | "wallet"
  | "suspensions"
  | "promos"
  | "sports-bets"
  | "withdrawals";

export type AalView = "events" | "agents";

export type AalFilterOption = {
  value: string;
  label: string;
};

export type AalFilterMeta = {
  key: string;
  multi_select?: boolean;
  options: Array<string | { value: string; label?: string }>;
  cascading_options?: Record<string, string[]> | null;
  default_values?: string[];
  selected_values?: string[];
};

export type AalPage<T> = {
  items: T[];
  total: number;
  page: number;
  size: number;
  pages: number;
  filters: AalFilterMeta[];
};

export type AalEqFilters = Record<string, string | undefined>;

export type AalListQuery = {
  start_date: string;
  end_date: string;
  page?: number;
  size?: number;
  sort?: string;
  filters?: AalEqFilters;
};

export type AalExportPayload = {
  start_date: string;
  end_date: string;
} & Record<string, string | number | undefined>;

export const AAL_DEFAULT_PAGE = 1;
export const AAL_DEFAULT_SIZE = 25;
export const AAL_EXPORT_MAX_INCLUSIVE_DAYS = 31;

const SECTION_PREFIX: Record<AalSection, string> = {
  all: "",
  wallet: "/wallet",
  suspensions: "/suspensions",
  promos: "/promos",
  "sports-bets": "/sports-bets",
  withdrawals: "/withdrawals",
};

const isPresent = (value: unknown): value is string =>
  typeof value === "string" && value.trim() !== "";

const finiteNumber = (value: unknown, fallback: number): number =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

export function aalListPath(section: AalSection, view: AalView): string {
  return `${API_ROUTES.AGENT_AUDIT_LOG_BASE}${SECTION_PREFIX[section]}/${view}`;
}

export function aalKpisPath(section: Exclude<AalSection, "all">): string {
  return `${API_ROUTES.AGENT_AUDIT_LOG_BASE}${SECTION_PREFIX[section]}/kpis`;
}

export function aalReasonsPath(decision: "approved" | "declined"): string {
  return `${API_ROUTES.AGENT_AUDIT_LOG_BASE}/withdrawals/reasons/${decision}`;
}

export function aalExportStartPath(
  section: AalSection,
  view: AalView,
): string {
  return `${aalListPath(section, view)}/exports`;
}

export function aalExportStatusPath(
  section: AalSection,
  view: AalView,
  exportId: string,
): string {
  return `${aalExportStartPath(section, view)}/${exportId}`;
}

export function buildAalEqParams(
  filters: AalEqFilters | undefined,
): Record<string, string> {
  const params: Record<string, string> = {};
  if (!filters) return params;
  for (const [key, value] of Object.entries(filters)) {
    if (isPresent(value)) {
      params[`${key}[eq]`] = value.trim();
    }
  }
  return params;
}

export function buildAalListParams(
  query: AalListQuery,
): Record<string, string | number> {
  const params: Record<string, string | number> = {
    start_date: query.start_date,
    end_date: query.end_date,
    page: query.page ?? AAL_DEFAULT_PAGE,
    size: query.size ?? AAL_DEFAULT_SIZE,
  };
  if (isPresent(query.sort)) {
    params.sort = query.sort.trim();
  }
  return { ...params, ...buildAalEqParams(query.filters) };
}

const EXPORT_OMITTED_KEYS = new Set([
  "start_date",
  "end_date",
  "page",
  "size",
  "sort",
  "view",
  "agent_performance_view",
  "page_size",
  "sort_by",
  "sort_order",
]);

export function buildAalExportBody(
  payload: AalExportPayload,
): AalExportPayload {
  const body: AalExportPayload = {
    start_date: payload.start_date,
    end_date: payload.end_date,
  };
  for (const [key, value] of Object.entries(payload)) {
    if (EXPORT_OMITTED_KEYS.has(key)) continue;
    if (isPresent(value)) {
      body[key] = value.trim();
    }
  }
  return body;
}

export function inclusiveDayCount(startDate: string, endDate: string): number {
  const start = Date.parse(`${startDate}T00:00:00Z`);
  const end = Date.parse(`${endDate}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
    return Number.POSITIVE_INFINITY;
  }
  return Math.floor((end - start) / 86_400_000) + 1;
}

export function isAalExportWindowValid(
  startDate: string,
  endDate: string,
): boolean {
  return inclusiveDayCount(startDate, endDate) <= AAL_EXPORT_MAX_INCLUSIVE_DAYS;
}

export function parseAalPage<T>(
  body: unknown,
  fallback: { page: number; size: number },
): AalPage<T> {
  const response = body as {
    data?: {
      items?: T[];
      total?: number;
      page?: number;
      size?: number;
      pages?: number;
    };
    meta?: { filters?: AalFilterMeta[] };
  } | null;
  const items = Array.isArray(response?.data?.items) ? response.data.items : [];
  const total = finiteNumber(response?.data?.total, items.length);
  const page = finiteNumber(response?.data?.page, fallback.page);
  const size = finiteNumber(response?.data?.size, fallback.size);
  const pages = finiteNumber(
    response?.data?.pages,
    size > 0 ? Math.ceil(total / size) : 0,
  );
  const filters = Array.isArray(response?.meta?.filters)
    ? response.meta.filters
    : [];
  return { items, total, page, size, pages, filters };
}

export function parseAalData<T>(body: unknown): T | null {
  const response = body as { data?: T } | null;
  return response?.data ?? null;
}

export function parseAalList<T>(body: unknown): T[] {
  const data = parseAalData<unknown>(body);
  if (Array.isArray(data)) return data as T[];
  if (
    data &&
    typeof data === "object" &&
    Array.isArray((data as { items?: unknown }).items)
  ) {
    return (data as { items: T[] }).items;
  }
  return [];
}

export function filterMetaByKey(
  filters: readonly AalFilterMeta[],
  key: string,
): AalFilterMeta | undefined {
  return filters.find((entry) => entry.key === key);
}

export function filterOptionsFor(
  filters: readonly AalFilterMeta[],
  key: string,
): AalFilterOption[] {
  const entry = filterMetaByKey(filters, key);
  if (!entry) return [];
  const options: AalFilterOption[] = [];
  for (const raw of entry.options ?? []) {
    if (typeof raw === "string") {
      options.push({ value: raw, label: raw });
      continue;
    }
    if (raw && typeof raw.value === "string") {
      options.push({
        value: raw.value,
        label: raw.label || raw.value,
      });
    }
  }
  return options;
}

export function cascadingOptionsFor(
  filters: readonly AalFilterMeta[],
  key: string,
): Record<string, string[]> {
  return filterMetaByKey(filters, key)?.cascading_options ?? {};
}

export async function fetchAalPage<T>(
  section: AalSection,
  view: AalView,
  query: AalListQuery,
): Promise<AalPage<T>> {
  const params = buildAalListParams(query);
  const response = await getPrivateInstance().get(aalListPath(section, view), {
    params,
  });
  return parseAalPage<T>(response?.data, {
    page: Number(params.page),
    size: Number(params.size),
  });
}

export async function fetchAalData<T>(
  path: string,
  params: Record<string, string | number>,
): Promise<T | null> {
  const response = await getPrivateInstance().get(path, { params });
  return parseAalData<T>(response?.data);
}

export function useAalListQuery<T>(
  section: AalSection,
  view: AalView,
  query: AalListQuery,
) {
  const params = buildAalListParams(query);
  const result = useQuery<AalPage<T>, Error>({
    queryKey: ["AGENT_AUDIT_LOG_LIST", section, view, params],
    queryFn: () => fetchAalPage<T>(section, view, query),
    enabled: Boolean(query.start_date && query.end_date),
    placeholderData: (previousData, previousQuery) => {
      const previousView = previousQuery?.queryKey[2];
      if (previousView !== view) return undefined;
      return previousData;
    },
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });

  return {
    ...result,
    items: result.data?.items ?? [],
    total: result.data?.total ?? 0,
    page: result.data?.page ?? query.page ?? AAL_DEFAULT_PAGE,
    size: result.data?.size ?? query.size ?? AAL_DEFAULT_SIZE,
    pages: result.data?.pages ?? 0,
    filters: result.data?.filters ?? [],
  };
}

export function useAalKpisQuery<T>(
  section: Exclude<AalSection, "all">,
  query: AalListQuery,
) {
  const params = {
    start_date: query.start_date,
    end_date: query.end_date,
    ...buildAalEqParams(query.filters),
  };
  return useQuery<T | null, Error>({
    queryKey: ["AGENT_AUDIT_LOG_KPIS", section, params],
    queryFn: () => fetchAalData<T>(aalKpisPath(section), params),
    enabled: Boolean(query.start_date && query.end_date),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
}

export function useAalExport(
  section: AalSection,
  view: AalView,
): AsyncExportState<AalExportPayload> {
  const exportState = useAsyncExport<AalExportPayload>({
    startPath: aalExportStartPath(section, view),
    statusPath: (exportId) => aalExportStatusPath(section, view, exportId),
    strings: {
      startError: AppStrings.PAM_AAL_EXPORT_START_ERROR,
      failedDefault: AppStrings.PAM_AAL_EXPORT_FAILED_DEFAULT,
      timeout: AppStrings.PAM_AAL_EXPORT_TIMEOUT,
    },
    queryKeys: {
      start: `AAL_EXPORT_START_${section}_${view}`,
      status: `AAL_EXPORT_STATUS_${section}_${view}`,
    },
  });

  return {
    ...exportState,
    start: (payload) => {
      if (!isAalExportWindowValid(payload.start_date, payload.end_date)) {
        message.error(AppStrings.PAM_AAL_EXPORT_DATE_WINDOW_TOO_LONG);
        return;
      }
      exportState.start(buildAalExportBody(payload));
    },
  };
}
